import type { Editor, RepresentationSpec } from '@aitomol/core';

/**
 * Helpers for editing the representation list.
 *
 * The LightCone skin owns `state.representations` too (Presentation cycles the
 * atom style, Polyhedra/H-bonds toggle decorations), so the platform panels must
 * add, patch and remove only their own entry and leave the rest untouched.
 */

export const ISOSURFACE = 'isosurface';
export const VIBRATION_VECTORS = 'vibration-vectors';

/** True when the installed backend knows how to draw this representation. */
export function supportsRepresentation(editor: Editor, type: string): boolean {
    return editor.representations.get(type) !== undefined;
}

export function representationOf(editor: Editor, type: string): RepresentationSpec | undefined {
    return editor.state.representations.find((spec) => spec.type === type);
}

export async function addRepresentation(editor: Editor, spec: RepresentationSpec): Promise<void> {
    const rest = editor.state.representations.filter((entry) => entry.type !== spec.type);
    await editor.setRepresentations([...rest, spec]);
}

export async function removeRepresentation(editor: Editor, type: string): Promise<void> {
    const rest = editor.state.representations.filter((entry) => entry.type !== type);
    if (rest.length === editor.state.representations.length) return;
    await editor.setRepresentations(rest);
}

/** Merges params into an existing spec, or adds the spec when it is missing. */
export async function patchRepresentation(
    editor: Editor,
    type: string,
    params: Record<string, unknown>,
    defaults: Record<string, unknown> = {}
): Promise<void> {
    const existing = representationOf(editor, type);
    if (!existing) {
        await addRepresentation(editor, { type, select: 'all', params: { ...defaults, ...params } });
        return;
    }
    const next = editor.state.representations.map((spec) =>
        spec.type === type ? { ...spec, params: { ...spec.params, ...params } } : spec
    );
    await editor.setRepresentations(next);
}